import React, { useEffect, useState } from 'react';
import { Button, Divider, Input, InputNumber, Modal, Pagination, Select, Spin, Table, Tag, Tooltip, Typography } from 'antd';
import { notification } from 'antd';
import { EditOutlined, EyeOutlined, LockFilled } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { actions, selectHeaders } from './Production.redux';
import useDebounce from 'hooks/useDebounce';
import ProductionModal from './ProductionModal';
import { EnterOutlined, BoxPlotOutlined, EditFilled, PlusOutlined } from '@ant-design/icons';
import ___ from '../constants';
import LoadingCover from 'components/LoadingCover';
import sizeSorter from '../shared/sizeSorter';
import sizeFilter from '../shared/sizeFilter';
import { extractColumnDataIndexes } from '../shared/searchUtilites';
import { useColumnSearch } from '../shared/useColumnSearch';
import { Link } from 'react-router-dom';
import NotesPopover from './NotesPopover';
import NotesModal from './NotesModal';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

const { Title } = Typography;

const DEBOUNCE_TIME = 900;

const isProductionOrderComplete = (r) => {
    if (r.fgCompletedQty >= r.fgPlannedQty) {
        return true;
    }
    return false;
};

const getProductionStatus = (r) => {
    if (r.isClosed === ___.TRUE) {
        return 'Closed';
    } if (r.isClosed === ___.FALSE && isProductionOrderComplete(r)) {
        return 'Completed';
    } else if (r.openLines > 0 && r.fgCompletedQty > 0) {
        return 'Partial';
    } else if (r.issuedQty > 0) {
        return 'Partial';
    } else if (r.openLines >= 0 && r.fgCompletedQty === 0) {
        return 'Pending';
    }
    return null;
};

const StatusRender = (_, r) => {
    switch (getProductionStatus(r)) {
        case 'Closed':
            return <Tag color="grey">Closed</Tag>;
        case 'Completed':
            return <Tag color="blue">Complete</Tag>;
        case 'Partial':
            return <Tag color="orange">Partial</Tag>;
        case 'Pending':
            return <Tag color="red">Pending</Tag>;
        default:
            return null;
    }
};

const sendToFilter = (value, record) => {
    // console.log(value);
    // console.log(record);
    if (value === 'pending' && record.sendToLocation === 'pending') {
        return true;
    } else if (value === 'pending' && !record.sendToLocation) {
        // additional case since pending isnt written to the UDF yet
        return true;
    } else if (value === 'qc' && record.sendToLocation === 'qc') {
        return true;
    } else if (value === 'on-draft' && record.sendToLocation === 'on-draft') {
        return true;
    } else if (value === 'bb' && record.sendToLocation === 'bb') {
        return true;
    } else if (value === 'sewing' && record.sendToLocation === 'sewing') {
        return true;
    } else if (value === 'photos-required' && record.sendToLocation === 'photos-required') {
        return true;
    } else if (value === 'upload-required' && record.sendToLocation === 'upload-required') {
        return true;
    }
};

const docStatusFilter = (value, record) => {
    return getProductionStatus(record) === value;
};

const DefaultWarehouseFilter = (value, record) => {
    if (value === 'All') return true;

    return record.defaultWarehouse === value;
};

const ProductionHeaders = () => {

    const dispatch = useDispatch(),
        navigate = useHistory(),
        [searchTerm, setSearchTerm] = useState(''),
        debouncedSearchTerm = useDebounce(searchTerm, DEBOUNCE_TIME),
        [notesVisible, setNotesVisible] = useState(false),
        [pendingSearch, setPendingSearch] = useState(null),
        [displayedRows, setDisplayedRows] = useState(null),
        [tableFilters, setTableFilters] = useState({

            // DISABLED - localStorage-based column filters are currently disabled for this page
            proStatus: localStorage.getItem('production_header_StatusFilter')
                ? false /* DISABLED [localStorage.getItem('production_header_StatusFilter')] */
                : null,
            defaultWarehouse: localStorage.getItem('production_header_WarehouseFilter')
                ? false /* DISABLED [localStorage.getItem('production_header_WarehouseFilter')] */
                : null,
            sendToLocation: localStorage.getItem('production_header_SendToFilter')
                ? false /* DISABLED [localStorage.getItem('production_header_SendToFilter')] */
                : null,
        }),
        [currentPage, setCurrentPage] = useState(1),
        [pageSize, setPageSize] = useState(50),
        warehouseOptions = useSelector(state => state.warehouseProd.warehouseOptions),
        failedDocEntriesBatch = useSelector(state => state.warehouseProd.failedDocEntriesBatch),
        errors = useSelector(state => state.warehouseProd.errors),
        loading = useSelector(state => state.warehouseProd.loading),
        sendingTo = useSelector(state => state.warehouseProd.sendingTo),
        searchLoading = useSelector(state => state.warehouseProd.searchLoading);

    const headers = useSelector(selectHeaders)

    const PROheaders = React.useMemo(() => {
        let rows = [...headers];

        // TODO if they ever need something like the saved filters in receiving
        // if (currentSendToFilter) {
        //     rows = rows.filter(record =>
        //         sendToFilter(currentSendToFilter, record)
        //     );
        // }

        // if (defaultWarehouseFilter) {
        //     rows = rows.filter(record =>
        //         DefaultWarehouseFilter(
        //             defaultWarehouseFilter,
        //             record
        //         )
        //     );
        // }

        if (searchTerm?.trim()) {
            const term = searchTerm.toLowerCase();

            rows = rows.filter(row =>
                [
                    // filter prop list
                    // TODO plumb up searchable
                    row.orderNum,
                    row.docNum,
                    row.origSO,
                    row.project,
                    row.fgProdName,
                    row.bpName,
                    row.shipper,
                ]
                    .filter(Boolean)
                    .some(v =>
                        String(v)
                            .toLowerCase()
                            .includes(term)
                    )
            );
        }

        return rows;
    }, [
        headers,
        //currentSendToFilter,
        //defaultWarehouseFilter,
        searchTerm,
    ]);


    // the search memo above changes the row set outside the table, so the
    // captured filtered/sorted order (and the page position) no longer apply
    useEffect(() => {
        setDisplayedRows(null);
        setCurrentPage(1);
    }, [searchTerm]);

    // total for the external pagination follows the table's current
    // filtered set when a column filter is active
    const totalRows = (displayedRows ?? PROheaders ?? []).length;

    const warehouseFilters = React.useMemo(() => {
        if (!warehouseOptions || warehouseOptions.length === 0) return [];

        return [
            { text: 'All', value: 'All' },
            ...warehouseOptions.map(opt => ({
                text: opt.FldValue,   // display
                value: opt.FldValue,  // filter value
            }))
        ];
    }, [warehouseOptions]);

    useEffect(() => {
        console.log('[ProductionHeaders.js] onload');
        dispatch(actions.get_warehouse_options());
        dispatch(actions.getProductionOrderHeaders());
    }, []);

    useEffect(() => {
        console.log('[ProductionHeaders.js] loading change: ' + loading);
        if (!loading && pendingSearch !== null) {
            onSearch(pendingSearch);
            setPendingSearch(null); // Clear it after running
        }
    }, [loading]);



    useEffect(() => {
        console.log('[ProductionHeaders.js] debouncedSearchTerm change');

        if (debouncedSearchTerm.trim() === '') {
            if (!loading) {
                //console.log('[ProductionHeaders.js] cleared search term');
                onSearch('');
            } else {
                setPendingSearch('');
            }
            return;
        }

        if (loading) {
            //console.log('[ProductionHeaders.js] Queuing search during loading');
            setPendingSearch(debouncedSearchTerm);
        } else {
            //console.log('[ProductionHeaders.js] Triggering onSearch for', debouncedSearchTerm);
            onSearch(debouncedSearchTerm);
        }
    }, [debouncedSearchTerm]);

    const { getColumnSearchProps } = useColumnSearch();


    const onSearch = (value) => {
        // console.log('onSearch ' + value);

        setSearchTerm(value);
        dispatch(actions.setSearchValue(value));
    };


    const handleSelectNotes = (record) => {
        console.log('handleSelectNotes', record)
        dispatch(actions.setCurrentEditRow(record));
        setNotesVisible(true)
    };


    const columns = [
        // { // DEBUG 
        //     title: 'KEY', dataIndex: 'docKey', key: 'docKey',
        //     render: (_, record) => (
        //         (record.hasChanges) ?
        //             <Tag color="red">{record.docKey}</Tag>
        //             :
        //             <Tag color="grey">{record.docKey}</Tag>
        //     ),
        // },
        {
            title: 'PRO#', dataIndex: 'orderNum', key: 'orderNum',
            width: 100,
            ...getColumnSearchProps('orderNum', 'PRO#'),
            filteredValue: tableFilters.orderNum ?? null,
        },
        {
            title: 'Original SO#',
            dataIndex: 'origSO',
            key: 'origSO',
            width: 100,
            render: (value, record, index) => (
                record.origSO ? (
                    <Link
                        to={`/orders/edit/order/${record.origSO}`} // WAS SODocEntry
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <Button>
                            {record.origSO}
                        </Button>
                    </Link>
                )
                    : (
                        <span style={{ display: 'block', textAlign: 'center' }}>-</span>
                    )
            ),
        },
        {
            title: 'Customer Name', dataIndex: 'bpName', key: 'bpName',
            width: 200, minWidth: 200, onHeaderCell: () => ({ style: { minWidth: 200 } }), onCell: () => ({ style: { minWidth: 200 } }),
            ...getColumnSearchProps('bpName', 'Customer', 168),
            filteredValue: tableFilters.bpName ?? null,
        },
        // floor: antd minWidth only applies with tableLayout auto; the header-cell min-width covers browsers that ignore min-width on col
        {
            title: 'Finished Product Name', dataIndex: 'fgProdName', key: 'fgProdName',
            width: 200, minWidth: 200, onHeaderCell: () => ({ style: { minWidth: 200 } }), onCell: () => ({ style: { minWidth: 200 } }),
            // in-cell floor for Firefox, same as the getColumnSearchProps cellMinWidth
            render: (text) => <div style={{ minWidth: 168 }}>{text}</div>,
        },

        {
            title: 'Project',
            dataIndex: 'project',
            key: 'project',
            render: (_, record) => {
                return record.project3 !== ''
                    ? record.project3 :
                    record.project2
                        ? record.project2
                        : record.project
                            ? record.project
                            : ___.NOT_AVAILABLE;
            },
        },
        {
            title: 'PRO Status', dataIndex: 'proStatus', key: 'proStatus',
            width: 200,
            align: 'center',
            filteredValue: tableFilters.proStatus ?? null,
            //defaultFilteredValue: ['Pending'],
            filters: [ //STATUS_FILTER_OPTIONS
                // {
                //     text: 'Open',
                //     value: 'Open',
                // },
                {
                    text: 'Pending',
                    value: 'Pending',
                },
                {
                    text: 'Partial',
                    value: 'Partial',
                },
                {
                    text: 'Closed',
                    value: 'Closed',
                },
                {
                    text: 'Completed',
                    value: 'Completed',
                },
            ],
            onFilter: docStatusFilter,
            render: StatusRender,
            filterMultiple: false,
            // ...getColumnSearchProps('proStatus', 'PRO Status'),
        },

        {
            //U_TS_DefaultWarehouse
            //defaultFilteredValue: ['BB'],
            title: 'Warehouse',
            dataIndex: 'defaultWarehouse',
            key: 'defaultWarehouse',
            filteredValue: tableFilters.defaultWarehouse ?? null,
            width: 80,
            filters: warehouseFilters,
            onFilter: DefaultWarehouseFilter,
            filterMultiple: false,
            render: (_, record) => {
                const match = warehouseOptions?.find(
                    opt => opt.FldValue === record.defaultWarehouse
                );

                return match ? (
                    <Tag color="default">{match.FldValue}</Tag>
                ) : (
                    <Tag color="error">None</Tag>
                );
            },
        },
        {
            title: 'Sent To',
            dataIndex: 'sendToLocation',
            key: 'sendToLocation',
            filteredValue: tableFilters.sendToLocation ?? null,
            filters: [
                {
                    text: 'Pending',
                    value: 'pending',
                },
                {
                    text: 'BB',
                    value: 'bb',
                },
                {
                    text: 'QC',
                    value: 'qc',
                },
                {
                    text: 'Sewing',
                    value: 'sewing',
                },
                {
                    text: 'On-Draft',
                    value: 'on-draft',
                },

                {
                    text: 'Fulfillment- Photos Required',
                    value: 'photos-required',
                },
                {
                    text: 'Fulfillment- Upload Required',
                    value: 'upload-required',
                },
            ],
            width: 240,
            onFilter: sendToFilter,
            filterMultiple: false,
            sortDirections: ['ascend', 'descend', 'ascend'],
            render: (_, record) =>
            (
                <>
                    <Select
                        value={record.sendToLocation || 'pending'}
                        style={{ width: 120 }}
                        onChange={(value) => sendTo(record, value)}>
                        <Select.Option value="pending">Pending</Select.Option>
                        <Select.Option value="bb">BB</Select.Option>
                        <Select.Option value="qc">QC</Select.Option>
                        <Select.Option value="sewing">Sewing</Select.Option>
                        <Select.Option value="on-draft">On-Draft</Select.Option>
                        <Select.Option value="photos-required">Fulfillment- Photos Required</Select.Option>
                        <Select.Option value="upload-required">Fulfillment- Upload Required</Select.Option>
                    </Select>
                    <Spin spinning={sendingTo} />
                </>
            ),
        },
        {
            title: 'Notes',
            dataIndex: 'add_edit_notes',
            key: 'add_edit_notes',
            width: 150,
            render: (_, record) => {
                return (
                    <Button
                        icon={<EyeOutlined />}
                        onClick={() => handleSelectNotes(record)} > {
                            'View Notes'
                        }</Button>
                )
            },
        },
        // { title: 'Item Code', dataIndex: 'itemCode', key: 'itemCode' },
        // { title: 'Over/Under', dataIndex: 'OverUnder', key: 'OverUnder' },
        // { title: 'Over/Under Line', dataIndex: 'OverUnder_RDR1', key: 'OverUnder_RDR1' },
        {
            title: 'Select / Edit',
            dataIndex: 'edit-select-button',
            render: (_, record) =>
            (
                <Button icon={<EditOutlined />} onClick={() => selectRow(record)} tabIndex={-1}>
                    Select / Edit
                </Button>
            ),
        },
        {
            title: 'Edit All from SO',
            dataIndex: 'select-all-so-button',
            render: (_, record) =>
            (
                <Button icon={<EditOutlined />} onClick={() => selectRowForSO(record)} tabIndex={-2}>
                    Select / Edit All from SO
                </Button>
            ),
        },
    ];

    const selectRow = (record) => {
        console.log('selectRow', record);
        navigate.push(`/warehouse/production/${record.docNum}`);
    };

    const selectRowForSO = (record) => {
        console.log('selectRowForSO', record);
        navigate.push(`/warehouse/production-from-so/${record.origSO}`); // ALT SODocEntry
    };

    let searchable = extractColumnDataIndexes(columns);
    //console.log(searchable);

    const sendTo = (record, value) => {
        console.log('sendTo', record, value);
        // if(value == 'pending'){
        //     return  null;
        // }
        dispatch(actions.writeSalesSendToFromheader(record, value));
    };


    const handleFocus = (event) => {
        console.log('handleFocus');
    };

    const handleBlur = (event) => {
        console.log('blur');
    };

    const handleChange = (event) => {
        //console.log('handleChange ', event.target.value);
        setSearchTerm(event.target.value);
    };

    const handleCloseNotes = () => {
        console.log('[ProductionHeaders] handleCloseNotes');
        setNotesVisible(false);
    };

    return (
        <>
            <Modal
                open={notesVisible}
                footer={null}
                onCancel={handleCloseNotes}
                mask={{ closable: false }}
                width={1200}
            >
                <NotesModal handleClose={handleCloseNotes} handleSave={null} viewOnly={true} />
            </Modal>

            <Title level={3}>Production</Title>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <Input.Search
                    name="production_id"
                    className="ts_disabled_alternate"
                    allowClear
                    enterButton
                    size="large"
                    placeholder="BP, Doc#, Finished Good, Item, or Project"
                    onSearch={onSearch}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    data-skip-blur-update={true}
                    value={searchTerm}
                    disabled={loading}
                    prefix={<Spin spinning={searchLoading} />}
                    style={{ flex: 1 }}
                />
                <Pagination
                    current={currentPage}
                    pageSize={pageSize}
                    total={totalRows}
                    pageSizeOptions={[10, 20, 50, 100]}
                    showSizeChanger
                    onChange={(page, size) => {
                        setCurrentPage(page);
                        setPageSize(size);
                    }}
                />
            </div>

            <Table
                scroll={{ x: 'max-content' }}
                tableLayout="auto"
                columns={columns}
                rowClassName="editable-row"
                dataSource={PROheaders}
                rowKey={'docKey'}
                // the pager renders in the search row above; placement 'none'
                // keeps the table paginating without its built-in pager
                pagination={{
                    placement: ['none'],
                    current: currentPage,
                    pageSize: pageSize,
                }}
                onChange={(pagination, filters, sorter, extra) => {
                    // column filters change the row count; capture the current set for the pager total
                    setDisplayedRows(extra?.currentDataSource || null);
                    setTableFilters(filters);

                    // DISABLED - did not request these column to be cached, but wanted to keep the code for reference, and in parity with similar components
                    // const _statusFilter = filters['proStatus']?.[0] || null;
                    // const _warehouseFilter = filters['defaultWarehouse']?.[0] || null;
                    // const _sendToFilter = filters['sendToLocation']?.[0] || null;

                    // localStorage.setItem('production_header_StatusFilter', _statusFilter || '');
                    // localStorage.setItem('production_header_WarehouseFilter', _warehouseFilter || '');
                    // localStorage.setItem('production_header_SendToFilter', _sendToFilter || '');

                    // antd resets current to 1 when a column filter changes
                    setCurrentPage(pagination.current);
                }}
            />

            {loading && <LoadingCover fixed />}

        </>
    );

};

export default ProductionHeaders;
